import moment, {Moment} from 'moment';
import {CareTask} from '../../../../../../../backend/interfaces/care-task';
import {CareTaskComplementary} from '../../../../../../../backend/interfaces/care-task-complementary';
import {User} from '../../../../../../../backend/interfaces/user';
import {AffiliatedUser} from '../../../../../../../backend/responses/affiliated-user';
import {CalendarData, CalendarDataEvent} from '../../../../../../../components/calendar/calendar';
import {createCareTaskInfo} from '../../../../../../../utils/data/care-task-info';
import {
  PlannerActivityItem,
  PlannerAnniversaryItem,
  PlannerAnniversaryUser,
  PlannerDate,
  PlannerItem,
  PlannerSection,
} from './planner-item';

export function transformEntityAnniversaries2PlannerAnniversaryItems(
  birthdaysData: User[],
  users: Record<string, AffiliatedUser>,
  startDate: Moment
): PlannerAnniversaryItem[] {
  return birthdaysData.map((birthdayUser) => {
    const birthday = moment(birthdayUser.birthday, 'YYYY-MM-DD').set({
      hour: 0,
      minute: 0,
      second: 0,
    });
    const age = birthday.diff(startDate, 'years') * -1;
    const birthdayThisYear = birthday.clone().year(startDate.year());
    const entityUser = users[birthdayUser.id!];

    const user: PlannerAnniversaryUser = {
      id: birthdayUser.id!,
      name: birthdayUser.name,
      age: age,
      birthday: birthday,
      gender: birthdayUser.gender,
      picture: birthdayUser.picture,
      socialResponses: entityUser?.affiliation.socialResponses,
    };

    const item: PlannerAnniversaryItem = {
      type: 'anniversary',
      user: user,
      id: birthdayUser.id! + '_' + birthdayUser.birthday!,
      date: {
        obj: birthdayThisYear,
        format: {
          dayOfWeek: birthdayThisYear.format('ddd'),
          day: birthdayThisYear.format('DD MMM YYYY'),
          hour: 'n/a',
        },
      },
    };

    return item;
  });
}
export function transformEntityCareTasks2PlannerActivityItems(
  careTasksInfo: CareTaskComplementary[],
  users: Record<string, AffiliatedUser>
): PlannerActivityItem[] {
  const output: PlannerActivityItem[] = [];

  for (const careTaskInfo of careTasksInfo) {
    const user = users[careTaskInfo.careTask.userId];

    // Create or Update a PlannerActivityItem for the user's care task.
    if (user) {
      // Skip inactive users
      if (user.user.isActive === false) {
        // ##############################################################
        // HACK: Backend should return care tasks from active users only
        // TODO: Remove this hack when backend is fixed
        // ##############################################################
        continue;
      }

      const date = moment(careTaskInfo.careTask.startsAt);
      const id = createPlannerActivityID(careTaskInfo.careTask, date);
      const foundItem = output.find((plannerCareTaskItem) => plannerCareTaskItem.id === id);

      // If the item already exists, we just update the info.
      if (foundItem) {
        const newItem = createPlannerActivityItem(careTaskInfo, [user]);
        foundItem.info.total += newItem.info.total;
        foundItem.info.handled += newItem.info.handled;
        foundItem.info.untreated += newItem.info.untreated;
        foundItem.users.push(...newItem.users);
        // sort users by user name.
        foundItem.users.sort((a, b) => a.name.localeCompare(b.name));
      }
      // If the item doesn't exist, we create a new one.
      else {
        const newItem = createPlannerActivityItem(careTaskInfo, [user]);
        output.push(newItem);
      }
    }
    // This is unexpected. We should always find the user.
    else {
      console.error(`Couldn't create an PlannerActivityItem: User not found. This is unexpected.`, {
        careTaskInfo,
        users,
      });
    }
  }

  return output;
}

/**
 * Returns an array of sections sorted by date(month & day).
 * Each section has a list of items(eg:activities/anniversaries)). These items are sorted by hour.
 */
export function transformPlannerItems2PlannerSections(items: PlannerItem[]): PlannerSection[] {
  const sectionsMap: Record<string, {items: PlannerItem[]; date: PlannerDate}> = {};

  for (const item of items) {
    const key = item.date.obj.format('YYYY-MM-DD');

    if (!sectionsMap[key]) {
      sectionsMap[key] = {
        items: [],
        date: item.date,
      };
    }

    sectionsMap[key].items.push(item);
  }

  return Object.entries(sectionsMap).map(([key, entry]) => ({
    id: key,
    date: entry.date,
    items: entry.items,
  }));
}

export function transformPlannerItems2CalendarData(items: PlannerItem[]): CalendarData {
  const events: CalendarDataEvent[] = [];
  let minDate: Moment = moment(items.length > 0 ? '23:59' : '06:00', 'HH:mm');

  for (const item of items) {
    let evt: CalendarDataEvent = {};

    if (item.type === 'activity') {
      const end = item.date.obj.clone().add(1, 'hour').isAfter(item.date.obj, 'day')
        ? item.date.obj.clone().endOf('day')
        : item.date.obj.clone().add(1, 'hour');

      // Calendar Event: https://fullcalendar.io/docs/event-object
      evt = {
        id: item.id,
        title: item.activitiesType.name,
        start: item.date.obj.format('YYYY-MM-DDTHH:mm'),
        end: end.format('YYYY-MM-DDTHH:mm'),
      };

      if (item.date.obj.hours() < minDate.hours()) {
        minDate = item.date.obj.clone().startOf('hour');
      }
    } else if (item.type === 'anniversary') {
      evt = {
        id: item.id,
        title: item.user.name,
        date: item.date.obj.toDate(),
        allDay: true,
        // Custom Property: https://fullcalendar.io/docs/event-object -> Non-standard Fields
        icon: 'brithday',
      };
    }

    events.push(evt);
  }

  return {
    events,
    minTime: minDate.format('HH:mm'),
  };
}

// ------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------

function createPlannerActivityItem(
  ctc: CareTaskComplementary,
  users: AffiliatedUser[]
): PlannerActivityItem {
  const today = moment();
  const date = moment(ctc.careTask.startsAt);
  const id = createPlannerActivityID(ctc.careTask, date);
  const markedAsHandled = ctc.careTask.markedAsHandled;
  const careTaskInfo = createCareTaskInfo(ctc, today);

  return {
    type: 'activity',
    id: id,
    entityId: ctc.careTask.entityId,
    date: createPlannerDate(date),
    activitiesSector: {
      id: ctc.careTask.activitiesSectorId,
    },
    activitiesGroup: {
      id: ctc.careTask.activitiesGroupId,
      name: ctc.activitiesGroupName,
    },
    activitiesType: {
      id: ctc.careTask.activityTypeId,
      name: ctc.careTask.nameConfigurationValue ?? ctc.activityTypeName,
    },
    info: {
      total: 1,
      handled: markedAsHandled ? 1 : 0,
      untreated: markedAsHandled ? 0 : date.isBefore(today) ? 1 : 0,
    },
    shiftIds: ctc.careTask.entityShiftIds,
    users: users.map((user) => ({
      id: user.user.id!,
      name: user.user.name,
      picture: user.user.picture,
      gender: user.user.gender,
      socialResponses: user.affiliation.socialResponses,
      careTask: careTaskInfo,
    })),
  };
}

function createPlannerDate(date: Moment): PlannerDate {
  return {
    obj: date,
    format: {
      dayOfWeek: date.format('ddd'),
      day: date.format('DD MMM YYYY'),
      hour: date.format('HH:mm'),
    },
  };
}

function createPlannerActivityID(careTask: CareTask, date: Moment): string {
  return (
    careTask.activitiesGroupId + '_' + careTask.activityTypeId + '_' + date.format('HHmm-DDMMYYYY')
  );
}
